import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';

// --- IMPORTS FOR IMAGES ---
import go_n from './Images/go_n.gif';
import go_o from './Images/go_o.gif';
import go_d from './Images/go_d.gif';
import close_n from './Images/close_n.gif';
import close_o from './Images/close_o.gif';
import close_d from './Images/close_d.gif';

const btnImages: Record<string, string> = {
    'go_n': go_n, 'go_o': go_o, 'go_d': go_d,
    'close_n': close_n, 'close_o': close_o, 'close_d': close_d
};

// --- Interfaces ---
interface TaskRecord {
    surname: string;
    address: string;
    suburb: string;
    postcode: string;
    recent_td: string;
    school_code: string;
    login: string;
}

interface SchoolOption {
    name: string;
    code: string;
}

// --- Helper: Legacy Image Button Component ---
const ImageButton: React.FC<{ name: string; onClick?: () => void; style?: React.CSSProperties }> = ({ name, onClick, style }) => {
    const [suffix, setSuffix] = useState('n');
    const imageKey = `${name}_${suffix}`;
    const imageSrc = btnImages[imageKey];

    return (
        <img 
            src={imageSrc} alt={name}
            style={{ cursor: 'pointer', verticalAlign: 'middle', ...style }}
            onMouseEnter={() => setSuffix('o')} 
            onMouseLeave={() => setSuffix('n')} 
            onMouseDown={() => setSuffix('d')}  
            onMouseUp={() => setSuffix('o')}    
            onClick={onClick} draggable={false}
        />
    );
};

const NoOpenTasks: React.FC = () => {
const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

    const getFormattedDate = (date: Date) => date.toISOString().split('T')[0];
    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);

    // --- State ---
    const [fromDate, setFromDate] = useState(getFormattedDate(lastWeek));
    const [toDate, setToDate] = useState(getFormattedDate(today));
    
    const [country, setCountry] = useState('---ALL---');
    const [countries, setCountries] = useState<string[]>([]);
    
    const [schoolCode, setSchoolCode] = useState('--Select--');
    const [schools, setSchools] = useState<SchoolOption[]>([]);
    
    const [data, setData] = useState<TaskRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // --- Effects ---
    useEffect(() => {
        document.body.style.backgroundColor = '#ffffff';

        // Fetch initial Countries
        const fetchCountries = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}api/get_dlr_countries`);
                const json = await res.json();
                if (json.countries) setCountries(json.countries);
            } catch (err) { console.error(err); }
        };
        fetchCountries();
    }, []);

    // Cascading Dropdown: Fetch Schools when Country changes
    useEffect(() => {
        const fetchSchools = async () => {
            setSchoolCode('--Select--');
            setSchools([]);
            
            if (country === '---ALL---') return;

            try {
                const res = await fetch(`${API_BASE_URL}api/get_schools_by_country?country=${country}`);
                const json = await res.json();
                if (json.schools) setSchools(json.schools);
            } catch (err) {
                console.error("School Fetch Error:", err);
            }
        };
        fetchSchools();
    }, [country]);

    // --- Handlers ---
    const handleGo = async () => {
        if (schoolCode === '--Select--') {
            alert('Please select a School Code.');
            return;
        }

        setLoading(true);
        setHasSearched(true);
        
        try {
            const res = await fetch(`${API_BASE_URL}api/no_open_tasks_report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    school_code: schoolCode, 
                    from_date: fromDate, 
                    to_date: toDate 
                })
            });
            const json = await res.json();
            
            if (json.error) alert(json.error);
            else setData(json.results || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        console.log("Closing No Open Tasks report window.");
        window.history.back(); 
    };

    const handleRowClick = (record: TaskRecord) => {
        const familyCode = "A";
        // Replicating legacy session string building: "&S=A&F=AA&L=[AdminLogin]"
        const sfl = "&S=A&F=AA&L=Admin"; 
        
        const url = `/Support/EL_AddEditTask?FamilyCode=${familyCode}&SchoolCode=${record.school_code}&Login=${record.login}&FilterType=CustEntry${sfl}`;
        window.location.href = url;
    };

    // --- Styles matching Legacy UI ---
    const styles = {
        body: { fontFamily: 'Arial, Helvetica, sans-serif', textAlign: 'center' as const, minHeight: '100vh', padding: '10px' },
        h1: { fontSize: '16px', fontWeight: 'bold', margin: '10px 0' },
        calContainer: { padding: '5px 0px 5px 50px', width: 'auto', textAlign: 'center' as const, marginBottom: '10px' },
        input: { fontFamily: 'Arial', fontSize: '13px', padding: '2px', border: '1px solid #7f9db9', width: '130px', marginRight: '5px' },
        select: { fontFamily: 'Arial', fontSize: '13px', padding: '2px', border: '1px solid #7f9db9', minWidth: '130px', marginLeft: '10px' },
        btnPlaceholder: { padding: '10px 0', textAlign: 'center' as const, marginBottom: '15px' },
        
        // Grid Styles
        gridWrapper: { overflowX: 'auto' as const, overflowY: 'auto' as const, maxHeight: '900px', width: '100%', border: '1px solid #072129' },
        table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '13px', fontFamily: 'Arial, sans-serif' },
        th: { backgroundColor: '#328da8', color: '#ffffff', padding: '5px 7px', border: '1px solid #072129', whiteSpace: 'nowrap' as const, fontWeight: 'bold', textAlign: 'left' as const },
        td: { padding: '5px 7px', border: '1px solid #072129', color: 'black', textAlign: 'left' as const },
        noRecords: { width: '600px', textAlign: 'center' as const, color: 'Black', fontSize: '20pt', margin: '20px auto' }
    };

    return (
        <div style={styles.body}>
            {loading && <Loading />}

            <h1 style={styles.h1}>No Open Tasks</h1>

            <div style={styles.calContainer}>
                <input type="date" style={styles.input} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                <span style={{ fontSize: '13px', margin: '0 5px' }}>To: </span>
                <input type="date" style={styles.input} value={toDate} onChange={(e) => setToDate(e.target.value)} />
                
                <select style={styles.select} value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="---ALL---">---ALL---</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>

                <select style={styles.select} value={schoolCode} onChange={(e) => setSchoolCode(e.target.value)}>
                    <option value="--Select--">--Select--</option>
                    {schools.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
            </div>

            <div style={styles.btnPlaceholder}>
                <ImageButton name="go" onClick={handleGo} style={{ marginRight: '10px' }} />
                <ImageButton name="close" onClick={handleClose} />
            </div>

            {!loading && hasSearched && data.length === 0 && (
                <div style={styles.noRecords}>No Records Found</div>
            )}

            {data.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div style={{ width: '80%', maxWidth: '1000px' }}>
                        <div style={styles.gridWrapper}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={{ ...styles.th, width: '40px', textAlign: 'center' }}>Sl No</th>
                                        <th style={styles.th}>Surname</th>
                                        <th style={styles.th}>Suburb</th>
                                        <th style={styles.th}>Postcode</th>
                                        <th style={styles.th}>Recent Task Dt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((row, idx) => (
                                        <tr 
                                            key={`${row.school_code}-${row.login}`} 
                                            style={{ backgroundColor: idx % 2 === 0 ? '#E5F2FF' : '#ffffff', cursor: 'pointer' }}
                                            onClick={() => handleRowClick(row)}
                                        >
                                            <td style={{ ...styles.td, textAlign: 'center' }}>{idx + 1}</td>
                                            <td style={styles.td}>{row.surname}</td>
                                            <td style={styles.td}>{row.suburb}</td>
                                            <td style={styles.td}>{row.postcode}</td>
                                            <td style={styles.td}>{row.recent_td}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NoOpenTasks;